/**
 * A page or component whose code could not be downloaded, in both apps (NEXT.md A1). Usually a
 * deploy replaced the chunk an open tab still names, or the network dropped.
 */
import * as Sentry from "@sentry/react";

export class RouteRenderError extends Error {
  override readonly name = "RouteRenderError";

  constructor(
    message: string = "A route failed to render",
    public readonly cause?: unknown
  ) {
    super(message);
    if (cause instanceof Error && cause.stack) {
      this.stack = `${this.stack}\nCaused by: ${cause.stack}`;
    }
    Object.setPrototypeOf(this, RouteRenderError.prototype);
  }
}

export class RouteChunkLoadError extends Error {
  override readonly name = "RouteChunkLoadError";

  constructor(
    message: string = "A page's code failed to download",
    public readonly cause?: unknown
  ) {
    super(message);
    if (cause instanceof Error && cause.stack) {
      this.stack = `${this.stack}\nCaused by: ${cause.stack}`;
    }
    Object.setPrototypeOf(this, RouteChunkLoadError.prototype);
  }
}

/** One Sentry issue for every failed download, whatever the chunk: a warning, not a crash. */
export const CHUNK_LOAD_FINGERPRINT = ["route-chunk-load-error"];

/**
 * Modules of two versions met in one tab: a module imports a name the other version no longer
 * exports (Chrome "does not provide an export named", Firefox "doesn't provide an export named" or
 * "ambiguous indirect export", Safari "Importing binding name 'X' is not found"). After a deploy
 * that is a tab holding some old modules; in development, files renamed while the dev server serves
 * the page. Reloading loads one version; the page's own code is not at fault.
 */
const isModuleMismatch = (error: Error): boolean =>
  error.name === "SyntaxError" &&
  /does(n't| not) provide an export named|ambiguous indirect export|Importing binding name .+ is not found/i.test(
    error.message
  );

/**
 * The page's code could not be loaded as one version: a native `import()` that could not fetch
 * its module (Chrome "Failed to fetch dynamically imported module", Firefox "error loading
 * dynamically imported module", Safari "Importing a module script failed"), Vite's "Unable to
 * preload CSS for …", webpack's `ChunkLoadError` ("Loading chunk 12 failed", from before the Vite
 * build; an open tab can still run that code), or modules of two versions (`isModuleMismatch`).
 */
export const isChunkLoadError = (error: unknown): error is Error =>
  error instanceof Error &&
  (error.name === "ChunkLoadError" ||
    isModuleMismatch(error) ||
    /Loading (CSS )?chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
      error.message
    ));

/**
 * Reports an error a lazy view's boundary caught: a failed download as the one chunk-load warning
 * (RouteChunkLoadError), anything else as the caller's own named error.
 */
export const captureLazyViewError = (
  error: unknown,
  toNamedError: (cause: unknown) => Error
) => {
  if (isChunkLoadError(error)) {
    Sentry.captureException(new RouteChunkLoadError(undefined, error), {
      level: "warning",
      fingerprint: CHUNK_LOAD_FINGERPRINT,
    });
    return;
  }
  Sentry.captureException(toNamedError(error));
};

/** A `preload()` that failed: reported, never left as an unhandled rejection; rendering retries. */
export const reportFailedPreload = (error: unknown) => {
  captureLazyViewError(
    error,
    (cause) => new RouteRenderError("A view's code failed to load", cause)
  );
};

const LAST_RELOAD_KEY = "genfive:staleChunkReloadAt";
/** A second failure this soon after the reload is not a stale tab: let the page say so. */
const RELOAD_WINDOW_MS = 30_000;

/** What a stale-code reload reads and does: when the tab last reloaded for it, and the reload. */
type StaleCodeTab = {
  sessionStorage: Pick<Storage, "getItem" | "setItem">;
  location: Pick<Location, "reload">;
};

type ReloadTarget = Pick<Window, "addEventListener"> & StaleCodeTab;

const readLastReload = (target: StaleCodeTab): number => {
  try {
    return Number(target.sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);
  } catch {
    return 0;
  }
};

/** True when the time was stored: only then can the next failure tell it already reloaded. */
const writeLastReload = (target: StaleCodeTab, at: number): boolean => {
  try {
    target.sessionStorage.setItem(LAST_RELOAD_KEY, String(at));
    return true;
  } catch {
    return false;
  }
};

/**
 * Reloads the tab, unless it already reloaded for stale code in the last 30 s: a second failure
 * that soon is real (offline, a broken deploy) and is left to the error page. With storage
 * blocked it never reloads by itself: nothing would remember the reload, and modules that still
 * mismatch after it would reload the tab forever; the error page's Reload button stays. True when
 * it reloads.
 */
export const reloadOnceForStaleCode = (
  target: StaleCodeTab = window,
  now: () => number = Date.now
): boolean => {
  const at = now();
  if (at - readLastReload(target) < RELOAD_WINDOW_MS) return false;
  if (!writeLastReload(target, at)) return false;
  target.location.reload();
  return true;
};

/**
 * Vite fires `vite:preloadError` when a lazy chunk or its CSS cannot be fetched. After a deploy
 * that is a tab running the previous build: reload it once (`reloadOnceForStaleCode`), so the user
 * gets the new build instead of an error; a second failure within 30 s throws, so the route's
 * error page asks the user to reload.
 */
export const reloadOnStaleChunks = (
  target: ReloadTarget = window,
  now: () => number = Date.now
) => {
  target.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleCode(target, now)) {
      event.preventDefault();
    }
  });
};
